import { Calendar, Tooltip } from 'antd';
import es from 'antd/es/locale/es_ES';
import { UserOutlined } from '@ant-design/icons';
import dayjs from 'libs/dayjs';
import { getStateConfig, stateConfig } from './stateConfig';

const MAX_DOTS = 6;

const DayCell = ({ date, events, isSelected }) => {
  if (!events || events.length === 0) return null;

  const visibleDots = events.slice(0, MAX_DOTS);
  const overflow = events.length - visibleDots.length;
  const firstCfg = getStateConfig(events[0].status);
  const firstName = events[0].Cliente?.name;

  const content = (
    <div className="flex flex-col items-start gap-0.5 w-full">
      <div className="flex items-center gap-0.5 flex-wrap">
        {visibleDots.map((ev) => {
          const cfg = getStateConfig(ev.status);
          return (
            <span
              key={ev.id}
              className="inline-block rounded-full"
              style={{ width: 5, height: 5, background: cfg.color }}
            />
          );
        })}
        {overflow > 0 && (
          <span
            className="text-[9px] font-medium ml-0.5"
            style={{ color: isSelected ? '#1e40af' : '#6b7280' }}
          >
            +{overflow}
          </span>
        )}
      </div>
      {firstName && (
        <span
          className="text-[10px] truncate w-full"
          style={{
            color: isSelected ? '#1e40af' : firstCfg.textColor,
            fontWeight: isSelected ? 500 : 400,
          }}
        >
          {firstName}
        </span>
      )}
    </div>
  );

  const tooltipContent = (
    <div className="space-y-1">
      {events.map((ev) => {
        const cfg = getStateConfig(ev.status);
        return (
          <div key={ev.id} className="flex items-center gap-2 text-xs text-white">
            <span
              className="inline-block rounded-full"
              style={{ width: 6, height: 6, background: cfg.color }}
            />
            <span className="font-medium">{ev.Cliente?.name}</span>
            {ev.hora_inicio && (
              <span className="text-gray-300">· {ev.hora_inicio.slice(0, 5)}</span>
            )}
            {ev.Specialist?.fullname && (
              <span className="text-gray-300 flex items-center">
                · <UserOutlined className="mx-1" /> {ev.Specialist.fullname}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );

  return (
    <Tooltip title={tooltipContent} placement="top">
      {content}
    </Tooltip>
  );
};

const MonthCalendarCompact = ({ selectedDay, onSelectDay, data }) => {
  const currentMonth = selectedDay || dayjs();
  const monthStart = currentMonth.startOf('month');
  const monthEnd = currentMonth.endOf('month');

  const monthEvents = (data || []).filter((item) => {
    const itemDate = dayjs(item.fecha_asignacion);
    return (
      (itemDate.isAfter(monthStart) || itemDate.isSame(monthStart, 'day')) &&
      (itemDate.isBefore(monthEnd) || itemDate.isSame(monthEnd, 'day'))
    );
  });

  const countsByStatus = Object.keys(stateConfig).reduce((acc, status) => {
    acc[status] = monthEvents.filter((ev) => ev.status === status).length;
    return acc;
  }, {});

  const cellRender = (date, info) => {
    if (info.type !== 'date') return info.originNode;
    const dayKey = date.format('YYYY-MM-DD');
    const events = (data || []).filter((item) => item.fecha_asignacion === dayKey);
    const isSelected = selectedDay?.format('YYYY-MM-DD') === dayKey;
    return <DayCell date={date} events={events} isSelected={isSelected} />;
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border p-4 space-y-3">
      <div className="flex flex-wrap gap-2 text-xs">
        {Object.entries(stateConfig).map(([status, cfg]) => (
          <span
            key={status}
            className="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5"
            style={{ background: cfg.bgColor, color: cfg.textColor }}
          >
            <span
              className="inline-block rounded-full"
              style={{ width: 6, height: 6, background: cfg.color }}
            />
            <span className="font-medium">{cfg.label}</span>
            <span className="opacity-75">· {countsByStatus[status]}</span>
          </span>
        ))}
      </div>

      <Calendar
        fullscreen
        cellRender={cellRender}
        value={currentMonth}
        onSelect={onSelectDay}
        locale={es}
      />
    </div>
  );
};

export default MonthCalendarCompact;
